"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pacsMappingCoverageFiguresSchema = exports.pacsMappingCoverageReasonSchema = exports.PACS_MAPPING_COVERAGE_REASONS = exports.pacsStepAssignInputSchema = exports.pacsProjectStepsInputSchema = exports.pacsApiSourceIdSchema = void 0;
exports.pacsAssignmentFor = pacsAssignmentFor;
exports.computeMappingCoverage = computeMappingCoverage;
const zod_1 = require("zod");
const common_js_1 = require("./common.js");
const pacs_sync_js_1 = require("./pacs-sync.js");
/**
 * Zuordnung von PACS-Schritten zu ASAS-Arbeitspaketen (AC-505).
 *
 * Transportfrei (ADR-04): nur Zod und reine Funktionen. Die Persistenz liegt in
 * `apps/api/src/pacs-mapping/` und `apps/api/src/pacs-sync/`.
 *
 * Identitaet eines Schritts ist seine numerische PACS-Id, nie Name oder Pfad:
 * PACS darf beides aendern, ohne dass eine getroffene Zuordnung verloren geht.
 */
/**
 * Obergrenze einer PACS-Id in einer API-EINGABE: die Spalten sind `INTEGER`.
 *
 * Nur fuer Eingaben von aussen. Ohne die Grenze kaeme eine zu grosse Zahl als
 * Datenbankfehler zurueck — also als 500 statt als 400. Das Spiegelschema aus
 * `pacs-sync.ts` bleibt davon unberuehrt; dort gilt, was PACS liefert.
 */
const POSTGRES_INTEGER_MAX = 2_147_483_647;
/** Eine PACS-Id, wie ein Aufrufer sie der API schicken darf. */
exports.pacsApiSourceIdSchema = pacs_sync_js_1.pacsSourceIdSchema.max(POSTGRES_INTEGER_MAX);
/**
 * `uuidSchema` und NICHT `idSchema`: `idSchema` ist bewusst undurchsichtig und
 * nimmt auch `WP-04` an (AC-015). Die Spalte ist aber `UUID`, und eine
 * Nicht-UUID kaeme erst als Datenbankfehler zurueck. Klein normalisiert, weil
 * der Dienst die Erwartung („gleiches Ziel") als Zeichenkette vergleicht.
 */
const workPackageIdSchema = common_js_1.uuidSchema;
exports.pacsProjectStepsInputSchema = zod_1.z.strictObject({
    projectPacsId: exports.pacsApiSourceIdSchema,
});
exports.pacsStepAssignInputSchema = zod_1.z.strictObject({
    stepPacsId: exports.pacsApiSourceIdSchema,
    workPackageId: workPackageIdSchema,
    /**
     * Optionaler Schutz gegen verlorene Aenderungen (CLAUDE.md §20.3): Was der
     * Aufrufer als AKTUELLES Ziel gesehen hat — `null` fuer „bisher nicht
     * zugeordnet". Weicht der Stand in der Datenbank ab, hat jemand anderes
     * inzwischen umgehaengt, und der Aufruf wird abgelehnt, statt dessen
     * Entscheidung still zu ueberschreiben. Steht die Zuordnung schon auf dem
     * Ziel, ist das eine Wiederholung und kein Konflikt — es wird nichts
     * ueberschrieben. Fehlt das Feld, wird nicht geprueft.
     */
    expectedWorkPackageId: workPackageIdSchema.nullable().optional(),
});
/**
 * Der Zuordnungszustand einer PACS-Buchung — die EINZIGE Stelle, an der er
 * abgeleitet wird.
 *
 *   ohne Schritt                  → `without_step`, kein Arbeitspaket
 *   Schritt mit Zuordnung         → `mapped`, dessen Arbeitspaket
 *   Schritt ohne Zuordnung        → `unmapped`, kein Arbeitspaket
 *
 * `mapped` entsteht damit ausschliesslich aus einer Schrittzuordnung. Bucht
 * PACS eine Buchung auf einen anderen Schritt um, folgt sie dessen Zuordnung;
 * verliert sie ihren Schritt, ist sie wieder `without_step`. Eine kuenftige
 * Zuordnung je einzelner Buchung (AC-308) braucht eine eigene Quelle, die
 * diese Funktion liest — sonst setzte der naechste Lauf sie zurueck.
 */
function pacsAssignmentFor(stepPacsId, mappedWorkPackageId) {
    if (stepPacsId === null)
        return { assignmentStatus: 'without_step', workPackageId: null };
    if (mappedWorkPackageId === null)
        return { assignmentStatus: 'unmapped', workPackageId: null };
    return { assignmentStatus: 'mapped', workPackageId: mappedWorkPackageId };
}
/**
 * Warum der Abdeckungsgrad keine Zahl ist — oder `null`, wenn er eine ist.
 *
 * Getrennt von `belowThreshold`, weil „keine Messung moeglich" und „zu
 * niedrig" verschiedene Handgriffe verlangen (dieselbe Linie wie `staleReason`
 * in AC-504).
 */
exports.PACS_MAPPING_COVERAGE_REASONS = ['no_work_packages'];
exports.pacsMappingCoverageReasonSchema = zod_1.z.enum(exports.PACS_MAPPING_COVERAGE_REASONS);
const countSchema = zod_1.z.number().int().nonnegative();
/**
 * Der Abdeckungsgrad nach AC-505, AK-1 und AK-2.
 *
 *   Einheit     ein INSTANZIIERTES Arbeitspaket (`work_packages`), jeder Status
 *   total       alle Arbeitspakete des Mandanten
 *   mapped      Arbeitspakete, auf die mindestens ein PACS-Schritt zeigt —
 *               jedes nur einmal, egal wie viele Schritte es speisen
 *   unmapped    total − mapped
 *   percent     mapped / total in Prozent, auf eine Nachkommastelle
 *               ABGESCHNITTEN; `null`, wenn total = 0
 *
 * Die Schwellenentscheidung rechnet ganzzahlig exakt (`mapped · 100 < total ·
 * thresholdPercent`) und nicht ueber `percent`: sonst hiesse 79,95 % gerundet
 * „80,0 %" und bestuende eine 80-%-Schwelle, die es verfehlt. Abgeschnitten
 * statt gerundet aus demselben Grund — die angezeigte Zahl darf nie besser
 * aussehen als die gepruefte.
 *
 * `thresholdPercent` ist die Schwelle DES MANDANTEN (AC-101), nicht eine
 * Konstante — die Antwort nennt sie mit, damit eine Oberflaeche nicht mit einer
 * eigenen, womoeglich veralteten Zahl vergleicht.
 *
 * Ohne Arbeitspakete gibt es keine Zahl, und keine Zahl ist keine Entwarnung:
 * `belowThreshold` ist dann `true`, `reason` sagt warum.
 */
exports.pacsMappingCoverageFiguresSchema = zod_1.z.strictObject({
    total: countSchema,
    mapped: countSchema,
    unmapped: countSchema,
    percent: zod_1.z.number().min(0).max(100).nullable(),
    belowThreshold: zod_1.z.boolean(),
    reason: exports.pacsMappingCoverageReasonSchema.nullable(),
    thresholdPercent: zod_1.z.number().int().min(0).max(100),
});
/**
 * Die Warnschwelle aus AK-2 kommt seit AC-101 aus der Konfiguration des
 * Mandanten (`thresholds.pacsMappingCoveragePercent`) und ist deshalb eine
 * PFLICHTANGABE. Bis AC-101 stand sie hier als Konstante (80) — fuer jeden
 * Mandanten gleich und nur ueber eine Codeaenderung verschiebbar. Die Vorgabe
 * fuer neue Mandanten ist weiterhin 80 und steht als Spaltenvorgabe in der
 * Migration `*_ac101_tenant_settings`, nicht hier: ein Standardwert an zwei
 * Orten waere die zweite Wahrheit.
 *
 * Liegt der Abdeckungsgrad darunter, wird das gemeldet, und der Jour Fixe
 * entscheidet, ob Arbeitspakete stattdessen in ASAS Core gepflegt werden. EINE
 * Schwelle, keine Automatik: unterhalb wird nichts geloescht, nichts
 * umgeschaltet und nichts automatisch zugeordnet.
 */
function computeMappingCoverage({ total, mapped, thresholdPercent, }) {
    if (!Number.isInteger(total) || !Number.isInteger(mapped) || total < 0 || mapped < 0) {
        throw new RangeError('Abdeckungsgrad: Zaehler und Nenner muessen ganze Zahlen >= 0 sein.');
    }
    if (mapped > total) {
        throw new RangeError('Abdeckungsgrad: mehr zugeordnete als vorhandene Arbeitspakete.');
    }
    if (!Number.isInteger(thresholdPercent) || thresholdPercent < 0 || thresholdPercent > 100) {
        throw new RangeError('Abdeckungsgrad: die Warnschwelle muss eine ganze Zahl von 0 bis 100 sein.');
    }
    if (total === 0) {
        return {
            total,
            mapped,
            unmapped: 0,
            percent: null,
            belowThreshold: true,
            reason: 'no_work_packages',
            thresholdPercent,
        };
    }
    return {
        total,
        mapped,
        unmapped: total - mapped,
        // Promille ganzzahlig abschneiden, dann in Prozent: 7995/10000 → 79,9.
        percent: Math.floor((mapped * 1000) / total) / 10,
        belowThreshold: mapped * 100 < total * thresholdPercent,
        reason: null,
        thresholdPercent,
    };
}
//# sourceMappingURL=pacs-mapping.js.map